import React, { useEffect, useState } from 'react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import BioCard from './about/BioCard';
import BioModal from './about/BioModal';
import { fetchAboutBios } from '../services/aboutService';
import { Bio, BioCategory, BioSection } from '../types/aboutBios';
import '../styles/about-page.css';
import '../styles/our-team-page.css';

const EXPANDED_CATEGORY_SLUG = 'core-founders';

export default function OurTeamPage() {
    const [bioSection, setBioSection] = useState<BioSection | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [activeBio, setActiveBio] = useState<Bio | null>(null);

    useEffect(() => {
        let isMounted = true;

        const loadBios = async () => {
            setIsLoading(true);
            setLoadError(false);

            const section = await fetchAboutBios();

            if (!isMounted) {
                return;
            }

            setBioSection(section);
            setLoadError(section === null);
            setIsLoading(false);
        };

        loadBios();

        return () => {
            isMounted = false;
        };
    }, []);

    const renderExpanded = (category: BioCategory) => (
        <section className="our-team-card our-team-founders" aria-labelledby={`team-${category.slug}`}>
            <h2 id={`team-${category.slug}`} className="our-team-section-heading">
                {category.name}
            </h2>
            <div className="our-team-founders-inner">
                {category.bios.map((bio) => (
                    <BioCard key={bio.id} bio={bio} />
                ))}
            </div>
        </section>
    );

    const renderCondensed = (category: BioCategory) => (
        <section className="our-team-card our-team-directors" aria-labelledby={`team-${category.slug}`}>
            <h2 id={`team-${category.slug}`} className="our-team-section-heading">
                {category.name}
            </h2>
            <div className="our-team-directors-grid">
                {category.bios.map((bio) => (
                    <button
                        key={bio.id}
                        type="button"
                        className="our-team-director-card"
                        onClick={() => setActiveBio(bio)}
                    >
                        {bio.imageUrl ? (
                            <img
                                src={bio.imageUrl}
                                alt={bio.title}
                                className="our-team-director-photo"
                                width={80}
                                height={80}
                            />
                        ) : (
                            <div className="our-team-director-photo-placeholder" aria-hidden="true">
                                <span>{bio.title.charAt(0)}</span>
                            </div>
                        )}
                        <span className="our-team-director-info">
                            <span className="our-team-director-name">{bio.title}</span>
                            {bio.position && (
                                <span className="our-team-director-role">{bio.position}</span>
                            )}
                            <span className="our-team-director-viewbio">View bio</span>
                        </span>
                    </button>
                ))}
            </div>
        </section>
    );

    return (
        <main className="about-page our-team-page">
            <PublicHeader />

            <section className="about-hero our-team-hero">
                <div className="about-hero-inner">
                    <h1 className="about-hero-heading">About</h1>
                    <p className="our-team-hero-sub">
                        Founded by educators with expertise in school and college counseling, higher
                        education, and the sports industry.
                    </p>
                </div>
            </section>

            <div className="our-team-content">
                {isLoading && (
                    <p className="about-bios-status" role="status">
                        Loading team bios...
                    </p>
                )}

                {!isLoading && loadError && (
                    <p className="about-bios-status about-bios-status-error" role="alert">
                        Unable to load team bios right now. Please try again later.
                    </p>
                )}

                {!isLoading && !loadError && bioSection?.categories.map((category) => {
                    if (!category.bios.length) {
                        return null;
                    }
                    return category.slug === EXPANDED_CATEGORY_SLUG
                        ? <React.Fragment key={category.id}>{renderExpanded(category)}</React.Fragment>
                        : <React.Fragment key={category.id}>{renderCondensed(category)}</React.Fragment>;
                })}

                {!isLoading && !loadError && bioSection?.categories.length === 0 && (
                    <p className="about-bios-status">No team bios are available yet.</p>
                )}
            </div>

            <BioModal bio={activeBio} onClose={() => setActiveBio(null)} />

            <PublicFooter />
        </main>
    );
}
